import React, { useState } from "react";
import "./step2.css";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { FaAngleRight } from "react-icons/fa";
import PhoneInput from "react-phone-number-input";
import CustomTitleSelect from "../CustomTitleComponent";
import CustomSelect from "../CustomSelect";
import { defaultGuest, Guest, Room } from "./HotelBookingPage";
import message from "antd/es/message";

interface Step2Props {
  checkIn: Date;
  checkOut: Date;
  selectedRoom: Room;
  occupancy: "Single" | "Double";
  setOccupancy: (val: "Single" | "Double") => void;
  rooms: number;
  setRooms: (val: number) => void;
  guests: Guest[];
  setGuests: React.Dispatch<React.SetStateAction<Guest[]>>;
  onNext: () => void;
  onBack: () => void;
}

const Step2: React.FC<Step2Props> = ({
  checkIn,
  checkOut,
  selectedRoom,
  occupancy,
  setOccupancy,
  rooms,
  setRooms,
  guests,
  setGuests,
  onNext,
  onBack,
}) => {
  const [currentGuestIndex, setCurrentGuestIndex] = useState(0);
  const [errors, setErrors] = useState<{ [key: number]: string[] }>({});

  const handleRoomChange = (value: number) => {
    setRooms(value);
    setGuests((prev) => {
      const updated = [...prev];
      if (value > prev.length) {
        for (let i = prev.length; i < value; i++) updated.push(defaultGuest());
      } else {
        updated.length = value;
      }
      return updated;
    });
    setCurrentGuestIndex(0);
  };

  const handleGuestInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setGuests((prev) => {
      const updated = [...prev];
      updated[currentGuestIndex] = {
        ...updated[currentGuestIndex],
        [name]: value,
      };
      return updated;
    });
  };

  const validateGuest = (guest: Guest) => {
    const missing: string[] = [];

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!guest.firstName) missing.push("First Name");
    if (!guest.lastName) missing.push("Last Name");
    if (!guest.mobile) missing.push("Mobile");
    if (!guest.email) {
      missing.push("Email");
    } else if (!emailRegex.test(guest.email)) {
      missing.push("Invalid Email");
    }

    return missing;
  };

  const handleNextGuest = () => {
    const validationErrors = validateGuest(guests[currentGuestIndex]);
    if (validationErrors.length) {
      setErrors((prev) => ({ ...prev, [currentGuestIndex]: validationErrors }));
      return;
    }
    setErrors((prev) => ({ ...prev, [currentGuestIndex]: [] }));
    setCurrentGuestIndex((prev) => prev + 1);
  };

  const handlePreviousGuest = () => {
    setCurrentGuestIndex((prev) => prev - 1);
  };

  // const handleFinalSubmit = () => {
  //   const validationErrors = validateGuest(guests[currentGuestIndex]);
  //   if (validationErrors.length) {
  //     setErrors((prev) => ({ ...prev, [currentGuestIndex]: validationErrors }));
  //     return;
  //   }
  //   onNext();
  // };
  const handleFinalSubmit = () => {
    const validationErrors = validateGuest(guests[currentGuestIndex]);
    if (validationErrors.length) {
      setErrors((prev) => ({ ...prev, [currentGuestIndex]: validationErrors }));
      return;
    }

    // Auto-set if only one guest
    if (guests.length === 1) {
      const updated = [...guests];
      updated[0].primary = true;
      setGuests(updated);
    }

    // Ensure at least one primary guest
    const hasPrimary = guests.some((g) => g.primary);
    if (!hasPrimary) {
      message.error("Please select a primary guest before continuing.");
      return;
    }

    onNext();
  };

  const nights =
    checkIn && checkOut
      ? (checkOut.getTime() - checkIn.getTime()) / (1000 * 3600 * 24)
      : 0;

  const totalPrice = guests.reduce((sum, guest) => {
    const rate =
      guest.occupancy === "Double" ? selectedRoom.double : selectedRoom.single;
    return sum + rate * nights;
  }, 0);

  const allGuestsComplete = guests.every((guest) => {
    return (
      guest.firstName.trim() &&
      guest.lastName.trim() &&
      guest.mobile.trim() &&
      guest.email.trim()
    );
  });

  const isMobile = window.innerWidth <= 768;
  return (
    <div className="step2-container">
      <div className="step2-content">
        <div className="room-info">
          <div className="room-info-card">
            {/* <img src={selectedRoom.image} alt={selectedRoom.name} /> */}

                  <div className="image-wrapper">
                    {(selectedRoom.limitSingle != null || selectedRoom.limitDouble != null) && (
                      <div className="badge-row">
                        <span className="badge limited">Limited Slots</span>
                      </div>
                    )}
                      <img src={selectedRoom.image} alt={selectedRoom.name} />
                  </div>
            <div className="content">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline", // key fix
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#022c22",
                  }}
                >
                  {selectedRoom.name.toUpperCase()}
                </h3>
                <a
                  style={{
                    fontSize: 12,
                    color: "#4f46e5",
                    textDecoration: "underline",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onClick={onBack}
                >
                  Change your selection
                </a>
              </div>

              <div className="prices">
                <div className="price-row">
                  <span>Single Occupancy</span>
                  <span className="price">
                    ${selectedRoom.single.toFixed(2)}
                  </span>
                </div>
                <div className="price-row">
                  <span>Double Occupancy</span>
                  <span className="price">
                    ${selectedRoom.double.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="room-info-card summary">
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#022c22",
              }}
            >
              Summary
            </h3>

            <div style={{ fontSize: 14, color: "#333" }}>
              <div className="prices">
                <div className="price-row">
                  <span>Total nights</span>
                  <span className="price">{nights} nights</span>
                </div>
                <div className="price-row">
                  <span>No. of Rooms</span>
                  <span className="price">{rooms}</span>
                </div>
              </div>

              {guests.map((guest, i) => {
                const rate =
                  guest.occupancy === "Double"
                    ? selectedRoom.double
                    : selectedRoom.single;
                const subtotal = rate * nights;

                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: "#ADADAD",
                      marginBottom: 6,
                      paddingLeft: 24,
                    }}
                  >
                    <span>
                      Room {i + 1} ({guest.occupancy})
                    </span>
                    <span>
                      ${rate.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      × {nights} nights ={" "}
                      ${subtotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                );
              })}

              <div
                style={{
                  borderTop: "1px solid #ddd",
                  marginTop: 12,
                  marginBottom: 8,
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#16a34a",
                }}
              >
                <span>Total</span>
                <span>
                  $
                  {totalPrice.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>

            <p
              className="note"
              style={{
                marginTop: 24,
                marginBottom: 0,
                fontSize: 13,
                color: "#666",
                textAlign: "center",
              }}
            >
              All rates include breakfast and applicable taxes.
            </p>
          </div>
        </div>

        <div className="form-container">
          <div className="form-row-dates-inline">
            <div className="form-group-dates">
              <label style={{ paddingBottom: 4 }}>Check-in Date</label>
              <label className="check-in-label">
                {" "}
                {checkIn.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "2-digit",
                })}
              </label>
            </div>
            <div className="form-group-dates">
              <label style={{ paddingBottom: 4 }}>Check-out Date</label>
              <label className="check-in-label">
                {" "}
                {checkOut.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "2-digit",
                })}
              </label>
            </div>
          </div>

          <div
            className="form-row"
            style={{
              display: "flex",
              flexDirection: "column",
              // gap: 10,
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: "0 0 180px" }}>
                <label
                  htmlFor="rooms"
                  style={{
                    display: "block",
                    fontWeight: 500,
                    marginBottom: 2,
                    width: isMobile ? "100%" : "320px",
                    fontSize: 14,
                  }}
                >
                  No. of Rooms
                </label>
                <CustomSelect
                  label=""
                  value={rooms.toString()}
                  options={["1", "2", "3", "4", "5"]}
                  onChange={(val) => handleRoomChange(Number(val))}
                />
              </div>

              <span
                className="room-note"
                style={{
                  fontSize: 14,
                  marginBottom: 4,
                }}
              >
                For group bookings over 5 rooms, contact{" "}
                <a href="mailto:accommodation@uaefma.com">
                  accommodation@uaefma.com
                </a>
              </span>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #ddd" }} />

          <div style={{ paddingTop: 10, paddingBottom: 10 }}>
            <h4 className="guest-title-details">Guest Details</h4>
            <span className="room-note">
              Please provide accurate information as per your passport
            </span>
          </div>

          <SwitchTransition mode="out-in">
            <CSSTransition
              key={currentGuestIndex}
              timeout={300}
              classNames="slide"
            >
              <div className="guest-form">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 24,
                  }}
                >
                  {/* Room Title */}
                  <h4
                    className="guest-title"
                    style={{ margin: 0, fontWeight: 700, color: "#003333" }}
                  >
                    Room {currentGuestIndex + 1} of {rooms}
                  </h4>

                  <div className="checkbox-row">
                    {/* <input
                      type="checkbox"
                      checked={guests[currentGuestIndex].primary || false}
                      disabled={guests.some(
                        (g, i) => g.primary && i !== currentGuestIndex
                      )}
                      onChange={() => {
                        const isAlreadyPrimary =
                          guests[currentGuestIndex].primary;
                        const updated = guests.map((g, i) => {
                          if (i !== currentGuestIndex)
                            return { ...g, primary: false };
                          return { ...g, primary: !isAlreadyPrimary };
                        });
                        setGuests(updated);
                      }}
                      className="checkbox-input"
                    /> */}
                    <input
                      type="checkbox"
                      checked={guests[currentGuestIndex].primary || false}
                      disabled={
                        guests.length === 1 ||
                        guests.some(
                          (g, i) => g.primary && i !== currentGuestIndex
                        )
                      }
                      onChange={() => {
                        const isAlreadyPrimary =
                          guests[currentGuestIndex].primary;
                        const updated = guests.map((g, i) => {
                          if (i !== currentGuestIndex)
                            return { ...g, primary: false };
                          return { ...g, primary: !isAlreadyPrimary };
                        });
                        setGuests(updated);
                      }}
                      className="checkbox-input"
                    />

                    <label
                      className="form-row checkbox-label"
                      style={{ paddingBottom: 6 }}
                    >
                      Set as Primary Guest
                    </label>
                  </div>
                </div>

                {/* Occupancy Field */}
                <div
                  className="form-row"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <label>Select occupancy</label>
                  <CustomSelect
                    label=""
                    options={["Single", "Double"]}
                    value={guests[currentGuestIndex].occupancy}
                    onChange={(val) => {
                      const updated = [...guests];
                      updated[currentGuestIndex].occupancy = val as
                        | "Single"
                        | "Double";
                      setGuests(updated);
                    }}
                  />
                </div>

                <div
                  className="form-row"
                  style={{
                    display: "flex",
                    gap: "24px",
                    marginBottom: "16px",
                    flexDirection: "row",
                  }}
                >
                  {/* Title Field */}
                  <div
                    style={{
                      flex: "none",
                      width: "120px", // or "30%", "150px" as needed
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <label>Title</label>
                    <CustomTitleSelect
                      value={guests[currentGuestIndex].title}
                      onChange={(value: string) => {
                        const updated = [...guests];
                        updated[currentGuestIndex].title = value;
                        setGuests(updated);
                      }}
                    />
                  </div>

                  {/* First Name */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <label>First Name (as per your passport)</label>
                    <input
                      name="firstName"
                      value={guests[currentGuestIndex].firstName}
                      onChange={handleGuestInputChange}
                      className={
                        errors[currentGuestIndex]?.includes("First Name")
                          ? "error"
                          : ""
                      }
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div className="form-row">
                  <label>Last Name (as per your passport)</label>
                  <input
                    name="lastName"
                    value={guests[currentGuestIndex].lastName}
                    onChange={handleGuestInputChange}
                    className={
                      errors[currentGuestIndex]?.includes("Last Name")
                        ? "error"
                        : ""
                    }
                  />
                </div>

                {/* Email */}
                <div className="form-row">
                  <label>Email Address</label>
                  <input
                    name="email"
                    value={guests[currentGuestIndex].email}
                    onChange={handleGuestInputChange}
                    className={
                      errors[currentGuestIndex]?.some((e) =>
                        e.includes("Email")
                      )
                        ? "error"
                        : ""
                    }
                  />
                  {errors[currentGuestIndex]?.includes("Email") && (
                    <div className="field-error">Email is required.</div>
                  )}
                  {errors[currentGuestIndex]?.includes("Invalid Email") && (
                    <div className="field-error">
                      Please enter a valid email address.
                    </div>
                  )}
                </div>

                {/* Mobile */}
                <div className="form-row">
                  <label>Mobile Number</label>
                  <div className="mobile-input">
                    <PhoneInput
                      international
                      defaultCountry="AE"
                      value={guests[currentGuestIndex].mobile}
                      onChange={(val) => {
                        const updated = [...guests];
                        updated[currentGuestIndex].mobile = val || "";
                        setGuests(updated);
                      }}
                      placeholder="Enter mobile number"
                      className={
                        errors[currentGuestIndex]?.includes("Mobile")
                          ? "input-phone input-error"
                          : "input-phone"
                      }
                      style={{ width: "100%" }}
                    />
                  </div>
                  {errors[currentGuestIndex]?.includes("Mobile") && (
                    <div className="field-error">
                      Mobile number is required.
                    </div>
                  )}
                </div>

                {/* Companion Fields (if Double) */}
                {guests[currentGuestIndex].occupancy === "Double" && (
                  <div
                    className="form-row-companion"
                    style={{ marginTop: 16, color: "#555" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        textAlign: "center",
                        margin: "32px 0 24px",
                      }}
                    >
                      <div
                        style={{
                          flex: "0 0 20px",
                          height: "1px",
                          backgroundColor: "#ccc",
                        }}
                      />
                      <span
                        style={{
                          padding: "0 12px",
                          fontWeight: 600,
                          color: "#444",
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Companion Details
                      </span>
                      <div
                        style={{
                          flex: 1,
                          height: "1px",
                          backgroundColor: "#ccc",
                        }}
                      />
                    </div>

                    <div
                      className="form-row"
                      style={{
                        display: "flex",
                        gap: "24px",
                        marginBottom: "16px",
                        flexDirection: "row",
                      }}
                    >
                      {/* Title Field */}
                      <div
                        style={{
                          flex: "none",
                          width: "120px", // or "30%", "150px" as needed
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <label>Title</label>
                        <CustomTitleSelect
                          value={
                            guests[currentGuestIndex].companionTitle || "Mr"
                          }
                          onChange={(value: string) => {
                            const updated = [...guests];
                            updated[currentGuestIndex].companionTitle = value;
                            setGuests(updated);
                          }}
                        />
                      </div>

                      {/* First Name */}
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <label>First Name (as per your passport)</label>
                        <input
                          name="companionFirstName"
                          value={
                            guests[currentGuestIndex].companionFirstName || ""
                          }
                          onChange={(e) => {
                            const updated = [...guests];
                            updated[currentGuestIndex].companionFirstName =
                              e.target.value;
                            setGuests(updated);
                          }}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <label>Last Name</label>
                      <input
                        name="companionLastName"
                        value={
                          guests[currentGuestIndex].companionLastName || ""
                        }
                        onChange={(e) => {
                          const updated = [...guests];
                          updated[currentGuestIndex].companionLastName =
                            e.target.value;
                          setGuests(updated);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CSSTransition>
          </SwitchTransition>

          <div className="guest-nav-buttons">
            {currentGuestIndex > 0 && (
              <button type="button" onClick={handlePreviousGuest}>
                ‹ Back to Guest #{currentGuestIndex}
              </button>
            )}

            {currentGuestIndex < rooms - 1 && (
              <button
                type="button"
                onClick={handleNextGuest}
                disabled={
                  !guests[currentGuestIndex].firstName.trim() ||
                  !guests[currentGuestIndex].lastName.trim() ||
                  !guests[currentGuestIndex].mobile.trim() ||
                  !guests[currentGuestIndex].email.trim()
                }
              >
                Next Guest <FaAngleRight />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Inline button group for desktop */}
      <div
        className="inline-button-group next-button"
        style={{
          backgroundColor: "#fff",
          justifyContent: "space-between",
          width: "95%",
        }}
      >
        <button onClick={onBack}>Back: Dates & Rooms</button>
        <button
          onClick={handleFinalSubmit}
          disabled={!allGuestsComplete}
          style={{
            opacity: allGuestsComplete ? 1 : 0.5,
            cursor: allGuestsComplete ? "pointer" : "not-allowed",
          }}
        >
          Next: Review Reservation
        </button>
      </div>

      {/* Floating button group for mobile */}
      <div className="floating-button-group">
        <button onClick={onBack}>Back</button>
        <button
          onClick={handleFinalSubmit}
          disabled={!allGuestsComplete}
          style={{
            opacity: allGuestsComplete ? 1 : 0.5,
            cursor: allGuestsComplete ? "pointer" : "not-allowed",
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Step2;
